import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFlow } from "../services/database";
import { Share2, XCircle, Maximize2, X, RefreshCcw } from "lucide-react";
import ShareModal from "../components/ShareModal";
import DesktopWarningBanner from "../components/DesktopWarningBanner";

// Helper that injects custom scrollbar CSS into HTML docs shown inside the iframe
const injectScrollbarStyles = (htmlContent) => {
  const scrollbarStyles = `
    <style>
      ::-webkit-scrollbar {
        width: 6px;
      }
      ::-webkit-scrollbar-track {
        background: transparent;
      }
      ::-webkit-scrollbar-thumb {
        background-color: rgba(100, 100, 100, 0.5);
        border-radius: 4px;
      }
      ::-webkit-scrollbar-thumb:hover {
        background-color: rgba(100, 100, 100, 0.7);
      }
      * {
        scrollbar-width: thin;
        scrollbar-color: rgba(100, 100, 100, 0.5) transparent;
      }
    </style>
  `;

  if (htmlContent.includes('<head>')) {
    return htmlContent.replace('<head>', '<head>' + scrollbarStyles);
  } else {
    return htmlContent.replace('<html>', '<html><head>' + scrollbarStyles + '</head>');
  }
};

// Enhanced script injector for rate-limits, link clicks, and form submissions
const injectRateLimitReporter = (htmlContent) => {
  const reporterScript = `
    <script>
      (function(){
        // Rate limit reporting for images
        const API_MARKER = '/api/v1/images/';
        window.addEventListener('error', function(e){
          const img = e.target;
          if (img && img.tagName === 'IMG' && img.src && img.src.includes(API_MARKER)) {
            fetch(img.src, { method: 'HEAD', cache: 'no-store' })
              .then(r => {
                if (r.status === 429) {
                  parent.postMessage({ type: 'IMAGE_RATE_LIMIT', src: img.src }, '*');
                }
              })
              .catch(()=>{});
          }
        }, true);

        // Intercept link clicks to prevent iframe navigation
        document.addEventListener('click', function(e) {
          const link = e.target.closest('a');
          if (link && link.href && !link.href.startsWith('#')) {
            e.preventDefault();
            e.stopPropagation();
            parent.postMessage({ type: 'LINK_CLICK', href: link.href }, '*');
          }
        }, true);
        
        // Intercept form submissions
        document.addEventListener('submit', function(e) {
          e.preventDefault();
          e.stopPropagation();
          parent.postMessage({ type: 'FORM_SUBMIT', action: e.target.action }, '*');
        }, true);
      })();
    </script>
  `;

  if (htmlContent.includes('</body>')) {
    return htmlContent.replace('</body>', reporterScript + '</body>');
  }
  return htmlContent + reporterScript;
};

export default function ViewFlow() {
  const location = useLocation();
  const navigate = useNavigate();

  const { flowId, projectId } = location.state || {};

  const [flowData, setFlowData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [deviceChoice, setDeviceChoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [isIframeHovered, setIsIframeHovered] = useState(false);
  
  // New state for error handling and oops messages
  const [error, setError] = useState(null);
  const [showOopsMessage, setShowOopsMessage] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null); // Reset error on new fetch
        
        if (!flowId || !projectId) {
          setError("Flow ID or Project ID is missing.");
          setIsLoading(false);
          return;
        }

        const flowResult = await getFlow(flowId);

        if (flowResult && flowResult.status === "success") {
          setFlowData(flowResult.flow);
          setDeviceChoice(
            flowResult.flow?.device?.toLowerCase() === "mobile"
              ? "Mobile"
              : "Desktop"
          );
        } else {
          setError("Failed to fetch flow data.");
        }
        
      } catch (err) {
        console.error("Error in ViewFlow fetchFlow:", err);
        setError("Failed to load flow. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [flowId, projectId]);

  // New useEffect to handle messages from the iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && (event.data.type === 'LINK_CLICK' || event.data.type === 'FORM_SUBMIT')) {
        setShowOopsMessage(true);
      }
    };
  
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading flow...</p>
        </div>
      </div>
    );
  }

  // Show general error message
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2 mx-auto"
          >
            <RefreshCcw size={20} />
            <span>Go Back</span>
          </button>
        </div>
      </div>
    );
  }

  // Show "Oops" message for intercepted links/forms
  if (showOopsMessage) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Oops! That Didn't Work</h2>
          <p className="text-gray-600 mb-6">
            Looks like the action you chose could not be accomplished. Click below to go back.
          </p>
          <button
            onClick={() => setShowOopsMessage(false)}
            className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2 mx-auto"
          >
            <RefreshCcw size={20} />
            <span>Go Back</span>
          </button>
        </div>
      </div>
    );
  }

  if (!flowData || deviceChoice === null) {
     return <div className="min-h-screen flex items-center justify-center">No flow data available.</div>;
  }

  const pages = flowData.pages || [];
  const pageTitle = flowData.title || "Untitled Flow";

  const handlePrev = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < pages.length - 1) setCurrentPage(currentPage + 1);
  };

  const handleClose = () => {
    navigate("/project-details", { state: { projectId } });
  };

  const openFullscreen = () => {
    setShowFullscreen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setShowFullscreen(false);
    document.body.style.overflow = 'auto';
  };

  const handleIframeMouseEnter = () => setIsIframeHovered(true);
  const handleIframeMouseLeave = () => setIsIframeHovered(false);

  const containerClasses =
    deviceChoice === "Mobile"
      ? "border rounded-md shadow-sm p-4 bg-white mx-auto max-w-sm"
      : "border rounded-md shadow-sm p-4 bg-white w-full";
      
  const currentHtml = pages[currentPage]?.html || '<html><body><p>No content for this page.</p></body></html>';
  const processedHtml = injectRateLimitReporter(injectScrollbarStyles(currentHtml));

  return (
    <div className="min-h-screen bg-white">
      <DesktopWarningBanner isDesktop={deviceChoice === "Desktop"} />
      <nav className="flex justify-between items-center p-3 border-b border-gray-300">
        <div className="text-xl font-bold text-gray-800">{pageTitle}</div>
        <div className="flex gap-2">
          <button
            onClick={handlePrev}
            disabled={currentPage === 0}
            className="p-2 bg-gray-800 text-white rounded disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={handleNext}
            disabled={currentPage === pages.length - 1}
            className="p-2 bg-gray-800 text-white rounded disabled:opacity-50"
          >
            Next
          </button>
          <button
            onClick={() => setShowShareModal(true)}
            className="text-gray-600 hover:text-black transition-colors duration-200"
            title="Share flow"
          >
            <Share2 size={24} />
          </button>
          <button
            onClick={handleClose}
            className="text-gray-600 hover:text-black transition-colors duration-200"
            title="Close"
          >
            <XCircle size={24} />
          </button>
        </div>
      </nav>

      <div className="max-w-8xl mx-auto p-4">
        <div 
          className={containerClasses + " overflow-y-auto relative"} 
          style={{ height: "80vh" }}
          onMouseEnter={handleIframeMouseEnter}
          onMouseLeave={handleIframeMouseLeave}
        >
          {isIframeHovered && (
            <button
              onClick={openFullscreen}
              className="absolute top-2 right-2 z-10 p-2 bg-black/60 hover:bg-black/90 rounded-md transition-all shadow-sm"
              title="Fullscreen"
            >
              <Maximize2 className="w-5 h-5 text-slate-300" />
            </button>
          )}
          {/* Iframe now shows only the current page's content */}
          <iframe
            key={currentPage} // Add key to force re-render on page change
            title={`FlowPage_${currentPage}`}
            srcDoc={processedHtml}
            style={{ width: "100%", height: "100%", border: "none" }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals"
          />
        </div>
      </div>

      {showShareModal && (
        <ShareModal
          itemType="flow"
          itemId={flowId}
          title={pageTitle}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showFullscreen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <div className="w-full h-full flex flex-col">
            <DesktopWarningBanner isDesktop={deviceChoice === "Desktop"} />
            <div className="bg-neutral-900 py-2 px-4 flex justify-between items-center">
              <div className="text-sm font-bold text-slate-300">{pageTitle}</div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={handlePrev}
                  disabled={currentPage === 0}
                  className="p-1 bg-slate-700 text-white rounded text-xs disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentPage === pages.length - 1}
                  className="p-1 bg-slate-700 text-white rounded text-xs disabled:opacity-50"
                >
                  Next
                </button>
                <button 
                  onClick={closeFullscreen}
                  className="text-slate-300 hover:text-white transition-colors ml-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className={`flex-1 overflow-hidden ${deviceChoice === 'Desktop' ? 'bg-white' : 'bg-black flex items-center justify-center'}`}>
              <div className={deviceChoice === 'Mobile' ? "border rounded-md shadow-sm p-4 bg-white mx-auto w-[384px]" : "w-full h-full"}>
                  <iframe 
                    key={`fullscreen-${currentPage}`}
                    srcDoc={processedHtml}
                    title="Fullscreen Flow View"
                    className="w-full border-0"
                    style={{ height: deviceChoice === 'Mobile' ? '80vh' : '100%' }}
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-presentation"
                  />
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}